import React from "react";
import { Tag, Users, Wallet, CalendarClock, UtensilsCrossed, Mail } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Switch } from "./ui/switch";

const DAYS = [["sun", "Sun"], ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"]];

export const DaySelect = ({ value, onChange, testId }) => (
  <div className="mt-3 flex flex-wrap gap-1.5 pl-12" data-testid={testId}>
    {DAYS.map(([k, label]) => (
      <button
        key={k}
        type="button"
        onClick={() => onChange(k)}
        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${value === k ? "bg-green-600 border-green-600 text-white" : "border-gray-300 text-gray-600 hover:border-green-500"}`}
        data-testid={`${testId}-${k}`}
      >
        {label}
      </button>
    ))}
  </div>
);

const MealSlots = ({ settings, onChange }) => {
  if (settings.plan_tier !== "paid") {
    return (
      <p className="mt-2 pl-12 text-xs text-gray-500" data-testid="meal-trial-note">
        Free trial: a dinner idea 3× a week (Mon/Wed/Fri) tailored to your allergies & diet. Subscribe to pick breakfast, lunch and dinner.
      </p>
    );
  }
  return (
    <div className="mt-3 pl-12 flex gap-3" data-testid="meal-slots">
      {["breakfast", "lunch", "dinner"].map((slot) => (
        <label key={slot} className="flex items-center gap-1.5 text-xs text-gray-700 capitalize">
          <Switch
            checked={settings.meal_slots?.[slot] ?? true}
            onCheckedChange={(v) => onChange("meal_slots", { [slot]: v })}
            data-testid={`toggle-meal-${slot}`}
          />
          {slot}
        </label>
      ))}
    </div>
  );
};

const TYPES = [
  { key: "deal_alerts", icon: Tag, title: "Grocery List Saver", desc: "When an item on your list goes on sale at a store within your radius", color: "bg-green-100 text-green-600" },
  { key: "community_deals", icon: Users, title: "Community Deals", desc: "When a new community deal is posted within your radius", color: "bg-blue-100 text-blue-600" },
  { key: "budget_deal_alerts", icon: Wallet, title: "Budget Friendly Deals", desc: "Morning picks near you that match what you usually buy", color: "bg-amber-100 text-amber-600" },
  { key: "budget_reminder", icon: CalendarClock, title: "Weekly Budget Reminder", desc: "Your budget, spent so far, what's left & deals that fit (6 PM)", color: "bg-purple-100 text-purple-600" },
  { key: "meal_alerts", icon: UtensilsCrossed, title: "Meal Alerts", desc: "What's for breakfast, lunch or dinner — based on your diet, allergies & list", color: "bg-rose-100 text-rose-600" },
  { key: "email_alerts", icon: Mail, title: "Email Alerts", desc: "Only used when push isn't set up on any of your devices", color: "bg-gray-100 text-gray-600" },
];

export default function AlertTypeSettings({ settings, onChange }) {
  return (
    <Card>
      <CardContent className="p-0 divide-y">
        {TYPES.map((t) => (
          <div key={t.key} className="p-4" data-testid={`setting-${t.key}`}>
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${t.color}`}>
                <t.icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{t.title}</p>
                <p className="text-xs text-gray-500">{t.desc}</p>
              </div>
              <Switch checked={!!settings[t.key]} onCheckedChange={(v) => onChange(t.key, v)} data-testid={`toggle-${t.key}`} />
            </div>
            {t.key === "budget_reminder" && settings.budget_reminder && (
              <DaySelect value={settings.budget_reminder_day} onChange={(d) => onChange("budget_reminder_day", d)} testId="budget-reminder-day" />
            )}
            {t.key === "meal_alerts" && settings.meal_alerts && <MealSlots settings={settings} onChange={onChange} />}
            {t.key === "email_alerts" && settings.email_alerts && settings.has_push_subscription && (
              <p className="mt-2 pl-12 text-xs text-gray-500" data-testid="email-push-note">Push is on, so alerts arrive as push notifications instead of email.</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
